<script setup>
    const { $bus } = useNuxtApp();
    
    const props = defineProps({
        speaker_id: {
            type: String,
            required: true
        },
        speaker: {
            type: Object,
            default: () => ({})
        }
    });

    const open = defineModel('open');
    const route = useRoute();
    const router = useRouter();
    
    const params = computed(() => { return { id: props.speaker_id, limit: 1 }} );
    
    const { data, pending, error, refresh } = await useAsyncData(`speaker-${props.speaker_id}`, () => $fetch(`/api/v2/speakers`, { params: { ...params.value } }))

    const page = computed(() => {
        if (!data.value?.speakers?.length) return false
        return { id: data.value.speakers[0].id, data: data.value.speakers[0], model: 'speakers' }
    })

    const back = () => {
        window.history.back()
    }

    const drawerStyles = {
        content: 'bg-primary border-0 rounded-none ring-teal-dark p-0 max-w-[calc(100%-0rem)]',
        overlay: 'bg-teal/90 backdrop-blur-xs',
        container: 'speaker-modal pb-[2.75rem] gap-0 bg-transparent',
        header: 'py-[3.125rem] px-[1.22875rem] md:px-[2.5rem] speaker-modal-header flex items-center justify-between',
        body: 'speaker-modal-body flex flex-col gap-0 p-[1.22875rem] md:p-[2.5rem] xl:max-w-[calc(100%-5dvw)] m-auto',
    }

    watch(() => props.speaker_id, (new_value) => {
        if (new_value) refresh();
    });

    const handleClose = () => {
        const currentQuery = { ...route.query };
        delete currentQuery.drawer;
        router.replace({ query: currentQuery });
    }

    const handleOpen = (val) => {
        if (val) { 
            $bus.emit('drawer-open');
        }
        if (!val) {
            $bus.emit('drawer-close');            
            handleClose();
        }
    }

</script>

<template>
    <UDrawer class="speaker-drawer" v-model:open="open" direction="right" :dismissable="true" :handle="false" :ui="drawerStyles" @update:open="handleOpen" :handleOnly="true">
        <template #header>
            <div class="inner">
                <UButton @click="back" aria-label="Go back to the previous page" variant="transparent" class="back-button">
                    <MessSvg type="back" />
                    Back
                </UButton>
                <UButton @click="open = false" aria-label="Close speaker details" icon="i-lucide-x" variant="close"></UButton>
            </div>
        </template>
        <template #body>
            <SpeakersModal :speaker="page.data" v-if="speaker_id && page?.data" :key="speaker_id" :class="pending ? 'blur-lg' : '' " />
        </template>
    </UDrawer>
</template>

<style lang="scss">

.speaker-drawer {
    z-index: 1;

    .inner {
        width: 100%;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;

        @media(min-width: $desktop) {
            max-width: calc(100% - 5dvw);
        }
    }
}

.speaker-modal-header {
    
    background-color: var(--teal);
    background-image: url('@/assets/img/backgrounds/bubble-teal-3-small.png');
    background-position: 75% top;
    background-repeat: no-repeat;

    @media(min-width: $tablet) {
        background-color: var(--teal);
        background-image: url('@/assets/img/backgrounds/bubble-teal-16.png');
        background-position: 75% top;
        background-repeat: no-repeat;
    }
}

.speaker-modal {

    .container {
        width: 100%;
        max-width: 100%;
        margin: 0;
    }

    .back-button {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        background-color: transparent;
        color: var(--sage);
        font-size: 0.8125rem;
        font-weight: 500;
        line-height: 1.25rem;
        letter-spacing: 0.040625rem;
        text-transform: uppercase;
        color: var(--sage);
        padding: 0;

        svg {
            stroke: var(--sage);
        }

        @include hover {
            color: var(--cream);
            text-decoration: underline;
            text-underline-offset: 0.25rem;

            svg {
                stroke: var(--cream);
            }            
        }
    }    
}

</style>